// src/components/CharacterEditorDialog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, TextField,
  FormControlLabel, Switch, Stack, CircularProgress,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { t } from "@lingui/macro";
import { getManifest, ensureAssetsManifest } from "../../utils/assetsManifest";

const FACE_PARTS = ["template", "eyes", "nose", "lips", "hair"];

/* ──────────────────────────────────────────────────────────────────────────────
   Image cache + helpers
   ────────────────────────────────────────────────────────────────────────────── */
const imgCache = new Map();
const loadImg = (url) =>
  new Promise((resolve, reject) => {
    if (!url) return resolve(null);
    if (imgCache.has(url)) return resolve(imgCache.get(url));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => { imgCache.set(url, img); resolve(img); };
    img.onerror = reject;
    img.src = url;
  });

const asHostingUrl = (p) =>
  !p ? "" : /^https?:\/\//i.test(p) ? p : (p.startsWith("/") ? p : "/" + p);

function listAllCharacterUrls(manifest) {
  const out = [];
  const chars = manifest?.characters || {};
  for (const gender of Object.keys(chars)) {
    const partsMap = chars[gender] || {};
    for (const part of Object.keys(partsMap)) {
      const arr = partsMap[part] || [];
      for (const rel of arr) out.push(asHostingUrl(rel));
    }
  }
  return out;
}
async function preloadAll(urls) {
  await Promise.all(urls.map((u) => loadImg(u).catch(() => { })));
}

/* ──────────────────────────────────────────────────────────────────────────────
   Ensure manifest ready
   ────────────────────────────────────────────────────────────────────────────── */
function useEnsureManifestLoaded() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await ensureAssetsManifest();
        if (active) setReady(true);
      } catch (e) {
        if (active) setError(e?.message || "Failed to load assets manifest.");
      }
    })();
    return () => { active = false; };
  }, []);
  return { ready, error };
}

/* ──────────────────────────────────────────────────────────────────────────────
   Aspect ratio hook — window.innerWidth / window.innerHeight
   ────────────────────────────────────────────────────────────────────────────── */
function useWindowAspectRatio() {
  const [ratio, setRatio] = useState(() => {
    if (typeof window === "undefined") return 1;
    const w = Math.max(1, window.innerWidth);
    const h = Math.max(1, window.innerHeight);
    return w / h;
  });

  useEffect(() => {
    const onResize = () => {
      const w = Math.max(1, window.innerWidth);
      const h = Math.max(1, window.innerHeight);
      setRatio(w / h);
    };
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return ratio;
}

/* ──────────────────────────────────────────────────────────────────────────────
   Arrow stepper
   ────────────────────────────────────────────────────────────────────────────── */
function ArrowStepper({ label, value = 0, max = 0, onChange, disabled, onScrub }) {
  const canUse = !disabled && max > 0;

  const goPrev = () => {
    if (!canUse) return;
    const next = (value - 1 + max) % max;
    onChange(next);
    onScrub?.();
  };
  const goNext = () => {
    if (!canUse) return;
    const nxt = (value + 1) % max;
    onChange(nxt);
    onScrub?.();
  };

  const counter = max > 0 ? `${Math.min(value + 1, max)}/${max}` : `0/0`;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", opacity: canUse ? 1 : 0.5 }}>
      <IconButton
        size="small"
        onClick={goPrev}
        disabled={!canUse}
        sx={{ color: "text.primary", "&.Mui-disabled": { color: "text.disabled" } }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Stack spacing={0.25} alignItems="center" justifyContent="center" sx={{ px: 0.5, lineHeight: 1.15 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: "100%", textAlign: "center" }}
          title={typeof label === "string" ? label : undefined}
        >
          {label}
        </Typography>
        <Typography variant="body2">{counter}</Typography>
      </Stack>

      <IconButton
        size="small"
        onClick={goNext}
        disabled={!canUse}
        sx={{ color: "text.primary", "&.Mui-disabled": { color: "text.disabled" } }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────────────────────────
   Main component
   ────────────────────────────────────────────────────────────────────────────── */
export default function CharacterEditorDialog({ open, initialValue, onClose, onSave, eagerPreload = true }) {
  const { ready, error } = useEnsureManifestLoaded();
  const [saving, setSaving] = useState(false);

  // aspect-ratio breakpoint: portrait-ish if < 0.6
  const aspect = useWindowAspectRatio();
  const isPortraitLayout = aspect < 0.6;

  // face zoom (only for FACE edits)
  const [zoomFace, setZoomFace] = useState(false);
  const zoomTimer = useRef(null);

  // state
  const [val, setVal] = useState(() => ({
    id: initialValue?.id || undefined,
    name: initialValue?.name || "",
    gender: initialValue?.gender || "female",
    template: initialValue?.template ?? 0,
    eyes: initialValue?.eyes ?? 0,
    nose: initialValue?.nose ?? 0,
    lips: initialValue?.lips ?? 0,
    hair: initialValue?.hair ?? 0,
    fullbody: initialValue?.fullbody ?? null,
    top: initialValue?.top ?? 0,
    bottom: initialValue?.bottom ?? 0,
  }));

  // Rehydrate only on closed → open. Ignore initialValue churn while closed.
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (open && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      console.count('[CharEditor] rehydrate ON OPEN');
      const init = initialValue || {};
      setVal({
        id: init.id || undefined,
        name: init.name || "",
        gender: init.gender || "female",
        template: init.template ?? 0,
        eyes: init.eyes ?? 0,
        nose: init.nose ?? 0,
        lips: init.lips ?? 0,
        hair: init.hair ?? 0,
        fullbody: init.fullbody ?? null,
        top: init.top ?? 0,
        bottom: init.bottom ?? 0,
      });
    }
    if (!open && hasOpenedRef.current) {
      hasOpenedRef.current = false;
      console.log('[CharEditor] closed; will rehydrate next time it opens');
    }
  }, [open]);


  const useFullbody = val.fullbody !== null && val.fullbody !== undefined;

  const isNpcTemplate = (val.template ?? 0) > 0;

  // manifest counts
  const counts = useMemo(() => {
    if (!ready) return null;
    try {
      const m = getManifest();
      const g = val.gender || "female";
      const safe = (part) => Math.max(0, (m?.characters?.[g]?.[part]?.length || 0));
      return {
        template: safe("template"),
        eyes: safe("eyes"),
        nose: safe("nose"),
        lips: safe("lips"),
        hair: safe("hair"),
        fullbody: safe("fullbody"),
        top: safe("top"),
        bottom: safe("bottom"),
      };
    } catch {
      return null;
    }
  }, [ready, val.gender]);

  const hasFullbody = !!(counts && counts.fullbody > 0);

  // auto-exit fullbody if unavailable for selected gender
  useEffect(() => {
    if (!counts) return;
    if (!hasFullbody && useFullbody) {
      setVal((p) => ({ ...p, fullbody: null, top: p.top ?? 0, bottom: p.bottom ?? 0 }));
    }
  }, [counts, hasFullbody, useFullbody]);

  // manifest URL helpers
  const urlForPart = (part, idx) => {
    try {
      const m = getManifest();
      const g = val.gender || "female";
      const list = m?.characters?.[g]?.[part] || [];
      const rel = list[idx] || "";
      return asHostingUrl(rel);
    } catch {
      return "";
    }
  };
  const urlForBackground1 = () => {
    try {
      const m = getManifest();
      const rel = (m.backgrounds || [])[0] || "";
      return asHostingUrl(rel);
    } catch {
      return "";
    }
  };

  // canvas rendering
  const canvasRef = useRef(null);
  const [rendering, setRendering] = useState(false);
  const TARGET_W = 816;
  const TARGET_H = 1456;

  const drawPreview = async () => {
    if (!ready) return;
    setRendering(true);
    try {
      const urls = isNpcTemplate
        ? [
          urlForBackground1(),
          urlForPart("template", val.template ?? 0),
        ]
        : [
          urlForBackground1(),
          urlForPart("template", val.template ?? 0),
          ...(useFullbody
            ? [urlForPart("fullbody", val.fullbody ?? 0)]
            : [
              urlForPart("bottom", val.bottom ?? 0),
              urlForPart("top", val.top ?? 0),
            ]),
          urlForPart("eyes", val.eyes ?? 0),
          urlForPart("nose", val.nose ?? 0),
          urlForPart("lips", val.lips ?? 0),
          urlForPart("hair", val.hair ?? 0),
        ];

      const imgs = (await Promise.all(urls.map(loadImg))).filter(Boolean);
      const c = canvasRef.current;
      if (!c) return;
      c.width = TARGET_W;
      c.height = TARGET_H;
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, TARGET_W, TARGET_H);

      const bg = imgs.shift();
      if (bg) {
        const sw = bg.naturalWidth || bg.width;
        const sh = bg.naturalHeight || bg.height;
        const scale = Math.max(TARGET_W / sw, TARGET_H / sh);
        const dw = Math.round(sw * scale);
        const dh = Math.round(sh * scale);
        const dx = Math.round((TARGET_W - dw) / 2);
        const dy = Math.round((TARGET_H - dh) / 2);
        ctx.drawImage(bg, dx, dy, dw, dh);
      }

      imgs.forEach((im) => ctx.drawImage(im, 0, 0, TARGET_W, TARGET_H));
    } finally {
      setRendering(false);
    }
  };

  useEffect(() => { if (open && ready) { drawPreview(); } }, [open, ready, val, useFullbody]);

  // Preload EVERYTHING only when explicitly allowed. Large manifests can freeze tabs.
  useEffect(() => {
    if (!open || !ready || !eagerPreload) return;
    let cancelled = false;
    (async () => {
      try {
        const m = getManifest();
        const urls = listAllCharacterUrls(m);
        const bg0 = (m.backgrounds || [])[0];
        if (bg0) urls.unshift(asHostingUrl(bg0));
        await preloadAll(urls);
      } catch { }
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [open, ready, eagerPreload]);


  // face zoom helpers
  const zoomOff = () => {
    if (zoomTimer.current) clearTimeout(zoomTimer.current);
    setZoomFace(false);
  };
  const triggerFaceZoom = () => {
    setZoomFace(true);
    if (zoomTimer.current) clearTimeout(zoomTimer.current);
    zoomTimer.current = setTimeout(() => setZoomFace(false), 4000);
  };

  // Preview viewport fills its column; column width is controlled by a clamp.
  const viewportStyle = {
    overflow: "hidden",
    width: "100%",
    border: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: 2,
    cursor: "zoom-in",
    background: (t) => t.palette.background.default,
    aspectRatio: "816 / 1456",
    position: "relative",
  };

  const faceZoomStyle = {
    transformOrigin: "50% 0%",
    transform: zoomFace ? "scale(2.0)" : "none",
    transition: "transform 120ms ease",
    willChange: "transform",
  };

  // toggle fullbody
  const onToggleFullbody = (checked) => {
    setVal((p) => ({
      ...p,
      fullbody: checked ? 0 : null,
      top: checked ? null : (p.top ?? 0),
      bottom: checked ? null : (p.bottom ?? 0),
    }));
  };

  // validation
  const validate = () => {
    const missing = [];

    if (!val.name?.trim()) missing.push("name");
    if (!val.gender) missing.push("gender");

    // Template is always required
    if (val.template === null || val.template === undefined) {
      missing.push("template");
    }

    // For NPC templates (template > 1), we do NOT require face/body parts.
    if (!isNpcTemplate) {
      // Face parts
      ["eyes", "nose", "lips", "hair"].forEach((k) => {
        const v = val[k];
        if (v === null || v === undefined) missing.push(k);
      });

      // Body parts
      if (useFullbody) {
        if (val.fullbody === null || val.fullbody === undefined) {
          missing.push("fullbody");
        }
      } else {
        if (val.top === null || val.top === undefined) missing.push("top");
        if (val.bottom === null || val.bottom === undefined) missing.push("bottom");
      }
    }

    return missing;
  };


  const handleSave = async () => {
    const missing = validate();
    if (missing.length) return;
    const payload = {
      id: val.id,
      name: val.name.trim(),
      gender: val.gender,
      template: Number(val.template ?? 0),
      eyes: Number(val.eyes ?? 0),
      nose: Number(val.nose ?? 0),
      lips: Number(val.lips ?? 0),
      hair: Number(val.hair ?? 0),
      fullbody: useFullbody ? Number(val.fullbody ?? 0) : null,
      top: useFullbody ? null : Number(val.top ?? 0),
      bottom: useFullbody ? null : Number(val.bottom ?? 0),
    };
    setSaving(true);
    try {
      await onSave?.(payload);
    } finally {
      setSaving(false);
    }
  };

  // gender change → reset all indices to defaults and exit zoom
  const handleGenderChange = (newGender) => {
    if (!newGender) return;
    setVal({
      id: val.id,
      name: val.name,
      gender: newGender,
      template: 0,
      eyes: 0,
      nose: 0,
      lips: 0,
      hair: 0,
      fullbody: null,
      top: 0,
      bottom: 0,
    });
  };

  if (!open) return null;

  const missing = validate();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus
      disableAutoFocus
      disableScrollLock
      transitionDuration={0}>
      <DialogContent sx={{ p: { xs: 1.25, sm: 2 } }} onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}>
        {!ready ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
            {error ? <Typography color="error">{error}</Typography> : <CircularProgress />}
          </Box>
        ) : (
          <Box
            sx={{
              display: isPortraitLayout ? "flex" : "grid",
              flexDirection: isPortraitLayout ? "row" : undefined,
              alignItems: isPortraitLayout ? "flex-start" : undefined,
              gap: isPortraitLayout ? 2 : undefined,
              gridTemplateColumns: !isPortraitLayout ? "auto 1fr" : undefined,
              columnGap: !isPortraitLayout ? 2 : undefined,
              rowGap: !isPortraitLayout ? 2 : undefined,
            }}
          >
            {/* LEFT column */}
            <Box
              sx={{
                ...(isPortraitLayout
                  ? {
                    flex: "0 0 clamp(120px, 44%, 360px)",
                    minWidth: 120,
                    maxWidth: "44%",
                  }
                  : {
                    width: "clamp(80px, 28vw, 300px)",
                  }),
                alignSelf: "flex-start",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box
                  sx={viewportStyle}
                  onClick={() => setZoomFace((z) => !z)}
                  title={t`Tap to toggle face zoom`}
                >
                  <div style={faceZoomStyle}>
                    <canvas
                      ref={canvasRef}
                      style={{
                        width: "100%",
                        height: "auto",
                        aspectRatio: "816 / 1456",
                        display: "block",
                      }}
                    />
                  </div>
                  <IconButton
                    aria-label={t`Toggle face zoom`}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setZoomFace((z) => !z); }}
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: 6,
                      bgcolor: "rgba(0,0,0,0.35)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {rendering && (
                <Box display="flex" alignItems="center" gap={1} sx={{ color: "text.secondary", mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">{t`Rendering…`}</Typography>
                </Box>
              )}

              {/* Name + Gender under avatar (portrait aspect only) */}
              <Box sx={{ mt: 1, display: isPortraitLayout ? "block" : "none" }}>
                <TextField
                  size="small"
                  label={t`Name`}
                  value={val.name}
                  onChange={(e) => { setVal((p) => ({ ...p, name: e.target.value })); }}
                  fullWidth
                  error={!val.name?.trim()}
                />
                <Box sx={{ mt: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={val.gender}
                    onChange={(_, v) => handleGenderChange(v)}
                    sx={{
                      width: "100%",
                      "& .MuiToggleButton-root": {
                        flex: 1,
                        textTransform: "none",
                        px: 1,
                        py: 0.5,
                        color: "text.primary",
                        borderColor: "divider",
                      },
                      "& .Mui-selected": {
                        bgcolor: "action.selected",
                        color: "text.primary",
                        "&:hover": { bgcolor: "action.selected" },
                      },
                    }}
                  >
                    <ToggleButton value="female">{t`Female`}</ToggleButton>
                    <ToggleButton value="male">{t`Male`}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* ✅ Portrait: Fullbody vs Top/Bottom as ToggleButtonGroup */}
                <Box sx={{ mt: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={useFullbody ? "fullbody" : "separate"}
                    onChange={(_, v) => {
                      if (!v) return;
                      onToggleFullbody(v === "fullbody");
                    }}
                    disabled={isNpcTemplate}

                    sx={{
                      width: "100%",
                      "& .MuiToggleButton-root": {
                        flex: 1,
                        textTransform: "none",
                        px: 1,
                        py: 0.5,
                        color: "text.primary",
                        borderColor: "divider",
                      },
                      "& .Mui-selected": {
                        bgcolor: "action.selected",
                        color: "text.primary",
                        "&:hover": { bgcolor: "action.selected" },
                      },
                    }}
                  >
                    <ToggleButton value="separate">{t`Top + Bottom`}</ToggleButton>
                    <ToggleButton value="fullbody" disabled={!hasFullbody}>
                      {t`Fullbody`}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>

            {/* RIGHT column */}
            <Box sx={{ alignSelf: "flex-start", minWidth: 0 }}>
              <Stack spacing={{ xs: 1.0, sm: 2 }}>
                {/* Name + Gender (landscape aspect only) */}
                <Box sx={{ display: isPortraitLayout ? "none" : "block" }}>
                  <Grid2 container spacing={1.0} alignItems="center">
                    <Grid2 xs={8}>
                      <TextField
                        size="small"
                        label={t`Name`}
                        value={val.name}
                        onChange={(e) => { setVal((p) => ({ ...p, name: e.target.value })); }}
                        fullWidth
                        error={!val.name?.trim()}
                      />
                    </Grid2>
                    <Grid2 xs={4}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={val.gender}
                        onChange={(_, v) => handleGenderChange(v)}
                        sx={{
                          width: "100%",
                          "& .MuiToggleButton-root": {
                            flex: 1,
                            textTransform: "none",
                            px: 1,
                            py: 0.5,
                            color: "text.primary",
                            borderColor: "divider",
                          },
                          "& .Mui-selected": {
                            bgcolor: "action.selected",
                            color: "text.primary",
                            "&:hover": { bgcolor: "action.selected" },
                          },
                        }}
                      >
                        <ToggleButton value="female">{t`Female`}</ToggleButton>
                        <ToggleButton value="male">{t`Male`}</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid2>
                  </Grid2>
                </Box>

                {/* Face — centered grid items */}
                <Box sx={{ p: 1, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t`Face`}</Typography>
                  <Grid2 container spacing={1.0} alignItems="center" justifyContent="center">
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Template`}
                        value={val.template ?? 0}
                        max={counts?.template ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, template: v })); triggerFaceZoom(); }}
                      />
                    </Grid2>
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Eyes`}
                        value={val.eyes ?? 0}
                        max={counts?.eyes ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, eyes: v })); triggerFaceZoom(); }}
                        disabled={isNpcTemplate}
                      />

                    </Grid2>
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Nose`}
                        value={val.nose ?? 0}
                        max={counts?.nose ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, nose: v })); triggerFaceZoom(); }}
                        disabled={isNpcTemplate}
                      />

                    </Grid2>
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Lips`}
                        value={val.lips ?? 0}
                        max={counts?.lips ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, lips: v })); triggerFaceZoom(); }}
                        disabled={isNpcTemplate}
                      />

                    </Grid2>
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Hair`}
                        value={val.hair ?? 0}
                        max={counts?.hair ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, hair: v })); triggerFaceZoom(); }}
                        disabled={isNpcTemplate}
                      />

                    </Grid2>
                  </Grid2>
                </Box>

                {/* Landscape: Fullbody vs Top/Bottom toggle between Face and Body */}
                <Box sx={{ display: isPortraitLayout ? "none" : "block" }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={useFullbody ? "fullbody" : "separate"}
                    onChange={(_, v) => {
                      if (!v) return;
                      onToggleFullbody(v === "fullbody");
                    }}
                    disabled={isNpcTemplate}

                    sx={{
                      width: "100%",
                      "& .MuiToggleButton-root": {
                        flex: 1,
                        textTransform: "none",
                        px: 1,
                        py: 0.5,
                        color: "text.primary",
                        borderColor: "divider",
                      },
                      "& .Mui-selected": {
                        bgcolor: "action.selected",
                        color: "text.primary",
                        "&:hover": { bgcolor: "action.selected" },
                      },
                    }}
                  >
                    <ToggleButton value="separate">{t`Top + Bottom`}</ToggleButton>
                    <ToggleButton value="fullbody" disabled={!hasFullbody}>
                      {t`Fullbody`}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Body — same centering approach */}
                <Box sx={{ p: 1, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t`Body`}</Typography>
                  <Grid2 container spacing={1.0} alignItems="center" justifyContent="center">
                    {hasFullbody && (
                      <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                        <ArrowStepper
                          label={t`Fullbody`}
                          value={val.fullbody ?? 0}
                          max={counts?.fullbody ?? 0}
                          onChange={(v) => { setVal((p) => ({ ...p, fullbody: v })); }}
                          onScrub={zoomOff}
                          disabled={!useFullbody || isNpcTemplate}
                        />
                      </Grid2>
                    )}
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Top`}
                        value={val.top ?? 0}
                        max={counts?.top ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, top: v })); }}
                        onScrub={zoomOff}
                        disabled={useFullbody || isNpcTemplate}
                      />
                    </Grid2>
                    <Grid2 xs={12} md={6} sx={{ display: "grid", placeItems: "center" }}>
                      <ArrowStepper
                        label={t`Bottom`}
                        value={val.bottom ?? 0}
                        max={counts?.bottom ?? 0}
                        onChange={(v) => { setVal((p) => ({ ...p, bottom: v })); }}
                        onScrub={zoomOff}
                        disabled={useFullbody || isNpcTemplate}
                      />
                    </Grid2>
                  </Grid2>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <Button onClick={onClose}>{t`Cancel`}</Button>
        <Button
          variant="contained"
          onClick={async () => { if (missing.length) return; await handleSave(); }}
          disabled={saving || !ready || missing.length > 0}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : t`Save`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
